import React from 'react';

import { includes } from 'lodash-es';
import { injectIntl, InjectedIntl } from 'react-intl';

import { StrategySelect } from 'src/sections/repository/components/strategy-select';
import { getBranchDialogHeight } from 'src/sections/repository/sections/branches/components/dialogs/utils';
import {
  getStrategyOption,
  MergeStrategyOption,
} from 'src/sections/repository/utils/merge-strategies';
import { MergeStrategy } from 'src/types/pull-request';

import { DetailedBranch } from '../../types';

import BranchDialog from './branch-dialog';
import messages from './merge-branch-dialog.i18n';

type Props = {
  intl: InjectedIntl;
  isOpen: boolean;
  isLoading: boolean;
  sourceBranchName?: string;
  destinationBranch?: DetailedBranch;
  sourceRepositoryFullName: string;
  destinationRepositoryFullName: string;
  commitMessage: string;
  onClose: () => void;
  onAction: (payload: object) => void;
  fetchDefaultCommitMessage: (payload: object) => void;
  isBannerOpen: boolean;
  canDeleteSourceBranch: boolean;
};

type State = {
  selectedMergeStrategy?: MergeStrategy;
  prevIsOpen: boolean;
};

class BaseBranchMergeDialog extends React.Component<Props, State> {
  // @ts-ignore TODO: fix noImplicitAny error here
  static getDefaultMergeStrategy(props) {
    if (!props.destinationBranch || !props.destinationBranch.merge_strategies) {
      return undefined;
    }

    const {
      default_merge_strategy: defaultMergeStrategy,
      merge_strategies: mergeStrategies,
    } = props.destinationBranch;

    if (
      !!getStrategyOption(defaultMergeStrategy, props.intl) &&
      includes(mergeStrategies, defaultMergeStrategy)
    ) {
      return defaultMergeStrategy;
    }

    if (includes(mergeStrategies, MergeStrategy.MergeCommit)) {
      return MergeStrategy.MergeCommit;
    }

    return mergeStrategies[0];
  }

  state: State = {
    selectedMergeStrategy: undefined,
    prevIsOpen: false,
  };

  static getDerivedStateFromProps(props: Props, state: State) {
    if (!state.selectedMergeStrategy) {
      return {
        selectedMergeStrategy:
          BaseBranchMergeDialog.getDefaultMergeStrategy(props),
      };
    }
    if (!state.prevIsOpen && props.isOpen) {
      return {
        prevIsOpen: true,
      };
    }
    if (state.prevIsOpen && !props.isOpen) {
      return {
        prevIsOpen: false,
        selectedMergeStrategy:
          BaseBranchMergeDialog.getDefaultMergeStrategy(props),
      };
    }
    return null;
  }

  // @ts-ignore TODO: fix noImplicitAny error here
  componentDidUpdate(prevProps) {
    if (prevProps.isOpen !== this.props.isOpen && this.props.isOpen) {
      this.fetchCommitMessage();
    }
  }

  getMergeStrategies(): MergeStrategy[] {
    const { destinationBranch } = this.props;
    if (!destinationBranch || !destinationBranch.merge_strategies) {
      return [];
    }
    const { merge_strategies: mergeStrategies } = destinationBranch;
    // Only include strategies defined in the MergeStrategy enum
    return mergeStrategies.filter(strategy =>
      Object.values(MergeStrategy).includes(strategy)
    );
  }

  handleChange = (selectedOption: MergeStrategyOption) => {
    this.setState(
      { selectedMergeStrategy: selectedOption.value },
      this.fetchCommitMessage
    );
  };

  shouldRemoveCommitMessageDiv() {
    return (
      this.state.selectedMergeStrategy === MergeStrategy.FastForward ||
      this.state.selectedMergeStrategy === MergeStrategy.RebaseFastForward
    );
  }

  // @ts-ignore TODO: fix noImplicitAny error here
  handleAction = payload => {
    this.props.onAction({
      ...payload,
      strategy: this.state.selectedMergeStrategy,
    });
  };

  fetchCommitMessage = () => {
    const { sourceBranchName, destinationBranch } = this.props;
    if (sourceBranchName && destinationBranch) {
      this.props.fetchDefaultCommitMessage({
        sourceBranchName,
        destinationBranchName: destinationBranch.name,
        mergeStrategy: this.state.selectedMergeStrategy,
      });
    }
  };

  render() {
    const { selectedMergeStrategy } = this.state;
    const mergeStrategies = this.getMergeStrategies();
    const shouldShowSelect = mergeStrategies.length > 1;
    const {
      destinationBranch,
      sourceRepositoryFullName,
      destinationRepositoryFullName,
      intl,
    } = this.props;

    return (
      <BranchDialog
        {...this.props}
        title={messages.dialogTitle}
        actionName={messages.actionName}
        isCommitMessageDivRemoved={this.shouldRemoveCommitMessageDiv()}
        childrenAboveCommitMsgDiv
        onAction={this.handleAction}
        height={getBranchDialogHeight(mergeStrategies, selectedMergeStrategy)}
        destinationBranchName={destinationBranch && destinationBranch.name}
        sourceRepositoryFullName={sourceRepositoryFullName}
        destinationRepositoryFullName={destinationRepositoryFullName}
        closeSourceBranchEnabled
      >
        {shouldShowSelect && (
          <StrategySelect
            isBannerOpen={this.props.isBannerOpen}
            label={intl.formatMessage(messages.mergeStrategySelectLabel)}
            strategies={mergeStrategies}
            value={
              selectedMergeStrategy
                ? getStrategyOption(selectedMergeStrategy, intl)
                : undefined
            }
            onChange={this.handleChange}
          />
        )}
      </BranchDialog>
    );
  }
}

export default injectIntl(BaseBranchMergeDialog);
